import Client from "../client";

const STORAGE_WORD = "CONFIG_SETTINGS";

// Add proper interfaces for invoice and stripe data
interface InvoiceData {
  _id: string;
  amount: number;
  status: string;
  createdAt: string;
  dueDate?: string;
  paidAt?: string;
  [key: string]: unknown;
}

interface StripeData {
  customerId?: string;
  subscriptionId?: string;
  [key: string]: unknown;
}

// Define proper UserData interface instead of any
export interface UserData {
  _id: string;
  sessionAccountId?: string;
  name: string;
  phone: string;
  email: string;
  gender: string;
  birthDate: string;
  groups: string[];
  effectiveGroups?: string[];
  verified?: boolean;
  createdAt: string;
  lastActivity?: string;
  accounts: string[];
  persona?: "legacy" | "nmg_b2c_teacher" | "nmg_school_admin" | "professor_school_global";
  accountsDetails: {
    _id: string;
    owner?: string;
    name: string;
    isPro: boolean;
    world: string;
    email: string;
    "new-manager-global"?: boolean;
    signupProfile?: "teacher" | "school";
    signupPlanKey?: string;
    signupPlanCycle?: "month" | "year";
    signupCurrency?: "brl" | "eur" | "usd";
    signupPlanCompletedAt?: string;
    onboardingCompletedAt?: string;
    aiCreditsMonthlyLimitOverride?: number;
    activityCoverDefaults?: {
      sourceMode: "subject" | "characters" | "mixed";
      categoryId?: string;
    };
    ultimaTempUnlock?: string;
    stripe?: StripeData;
    invoices?: InvoiceData[];
  }[];
  intercomHash: string;
  preferredLanguage?: string;
}

export type ISettings = {
  qrid: boolean;
  rfid: boolean;
  simple: boolean;
  student_email_login_enabled?: boolean;
  username: string;
  userData: UserData;
  intercomHash: string;
  feed: boolean;
  isNewAuth: boolean;
  sound_disabled: boolean;
  ranking_disabled: boolean;
  shopping_disabled: boolean;
  hide_coins: boolean;
  disabled: boolean;
  availablePoints: string[];
  points: {
    id: string;
    name: string;
    source: string;
  }[];
  invoices: InvoiceData[];
  schoolName: string;
  game_controlado: boolean;
  max_professor_points: number;
  professor_can_select_classes: boolean;
  professor_missions: boolean;
  missoes2: boolean;
  missoes2ComprovantesComIa: boolean;
  light: boolean;
  plano_ia: boolean;
  levels: Record<
    string,
    {
      id: string;
      name: string;
      order: number;
      source: string;
      objective: {
        id: string;
        objectiveId: string;
        name: string;
        desc: string;
      };
      range: {
        min: number;
        max: number;
      };
      rewards: {
        coin: number;
      };
    }[]
  >;
  is_pro: boolean;
  setup_completed: boolean;
  purchasesCounter: number;
  isAdmin: boolean;
  worldId: string;
  isCompleteRegister: boolean;
  accountsIds: string[];
  accountsDetails: {
    _id: string;
    owner?: string;
    groups: string[];
    name: string;
    isPro: boolean;
    world: string;
    email: string;
    "new-manager-global"?: boolean;
    signupProfile?: "teacher" | "school";
    signupPlanKey?: string;
    signupPlanCycle?: "month" | "year";
    signupCurrency?: "brl" | "eur" | "usd";
    signupPlanCompletedAt?: string;
    onboardingCompletedAt?: string;
    aiCreditsMonthlyLimitOverride?: number;
    activityCoverDefaults?: {
      sourceMode: "subject" | "characters" | "mixed";
      categoryId?: string;
    };
    ultimaTempUnlock?: string;
    stripe?: StripeData;
    invoices?: InvoiceData[];
  }[];
};

export type AuthoritativeAccountContext = {
  settings: ISettings;
  userInfo: UserData;
};

export default class Settings {
  static cachedSettings: ISettings | undefined;
  static loadSettingsPromise: Promise<ISettings | undefined> | undefined;
  static loadAuthoritativeAccountContextPromise: Promise<AuthoritativeAccountContext | undefined> | undefined;
  static loadAuthoritativeAccountContextPromiseKey: string | undefined;
  private static subscribers = new Set<() => void>();

  static subscribe(listener: () => void): () => void {
    this.subscribers.add(listener);
    return () => this.subscribers.delete(listener);
  }

  private static notifySubscribers(): void {
    this.subscribers.forEach(listener => listener());
  }

  static async loadSettings(): Promise<ISettings | undefined> {
    if (typeof window === "undefined") return;

    // Prevent duplicate calls - return existing promise if already loading
    if (this.loadSettingsPromise) {
      return this.loadSettingsPromise;
    }

    this.loadSettingsPromise = this._loadSettings();
    return this.loadSettingsPromise;
  }

  /**
   * Carrega a identidade da conta ativa pela fonte autoritativa real.
   * Contas legacy não recebem `accountsDetails` em `/settings`, portanto
   * precisam combinar esse snapshot com `/users/info`.
   */
  static loadAuthoritativeAccountContext(): Promise<AuthoritativeAccountContext | undefined> {
    const requestKey = this.getCachedSessionIdentity();
    if (this.loadAuthoritativeAccountContextPromise && this.loadAuthoritativeAccountContextPromiseKey === requestKey) {
      return this.loadAuthoritativeAccountContextPromise;
    }

    const request = this._loadAuthoritativeAccountContext().finally(() => {
      if (this.loadAuthoritativeAccountContextPromise !== request) return;

      this.loadAuthoritativeAccountContextPromise = undefined;
      this.loadAuthoritativeAccountContextPromiseKey = undefined;
    });
    this.loadAuthoritativeAccountContextPromise = request;
    this.loadAuthoritativeAccountContextPromiseKey = requestKey;
    return request;
  }

  private static getCachedSessionIdentity(): string {
    const settings = this.getSettings();
    if (!settings) return "missing";

    return `present:${settings.worldId ?? ""}:${settings.schoolName ?? ""}`;
  }

  private static async _loadAuthoritativeAccountContext(): Promise<AuthoritativeAccountContext | undefined> {
    const client = new Client();
    const settings = await client.getSettings();
    if (!settings) return undefined;

    const userInfo = await client.getLoggedUserData();
    if (!userInfo) return undefined;

    return { settings, userInfo };
  }

  private static async _loadSettings(): Promise<ISettings | undefined> {
    try {
      const client = new Client();
      const settings = await client.getSettings();
      if (!settings) {
        return;
      }
      const purchasesCounter = await client.getShoppingPurchasesCount("created");
      settings.purchasesCounter = purchasesCounter;
      settings.isAdmin = true;
      if (settings.isNewAuth) {
        const userData = await client.getLoggedUserData();
        if (!userData) return;
        settings.isCompleteRegister = (userData.name && userData.birthDate && userData.gender) !== undefined;
        settings.isAdmin = userData?.groups.includes("admin");
        settings.accountsIds = userData.accounts;
        settings.accountsDetails = userData.accountsDetails;
        settings.intercomHash = userData.intercomHash;
        settings.userData = userData;
      }
      localStorage.setItem(STORAGE_WORD, JSON.stringify(settings));
      this.cachedSettings = settings;
      this.notifySubscribers();

      void import("@/lib/posthog-enrichment").then(mod => {
        mod.refreshPostHogContext({ force: true, mode: "full" });
      });

      return settings;
    } finally {
      this.loadSettingsPromise = undefined;
    }
  }

  static async unloadSettings() {
    if (typeof window === "undefined") return;

    localStorage.removeItem(STORAGE_WORD);
    this.cachedSettings = undefined;
    this.notifySubscribers();
  }

  static setSettings(settings: ISettings) {
    if (typeof window === "undefined") return;
    localStorage.setItem(STORAGE_WORD, JSON.stringify(settings));
    this.cachedSettings = settings;
    this.notifySubscribers();
  }

  static getSettings(): ISettings | undefined {
    if (typeof window === "undefined") return undefined;
    if (this.cachedSettings) return this.cachedSettings;
    const settings = localStorage.getItem(STORAGE_WORD);
    if (settings) {
      try {
        const parsedSettings = JSON.parse(settings) as ISettings;
        this.cachedSettings = parsedSettings;
        return parsedSettings;
      } catch (error) {
        // Auto-heal: limpeza de cache corrompido evita tela presa em loading.
        console.warn("Invalid cached settings found. Resetting local settings cache.", error);
        localStorage.removeItem(STORAGE_WORD);
        this.cachedSettings = undefined;
        return undefined;
      }
    }
    return undefined;
  }

  static isEnable(key: keyof ISettings) {
    if (typeof window === "undefined") return;

    try {
      const settings = this.getSettings();
      if (!settings) {
        this.loadSettings();
      }
      return Boolean(settings?.[key]);
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  static getData(key: keyof ISettings) {
    if (typeof window === "undefined") return;
    try {
      const settings = this.getSettings();
      if (!settings) {
        this.loadSettings();
      }
      return settings?.[key];
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}
